/**
 * Route paths (ARCHITECTURE §12) and typed route-param hooks. Build every in-app link
 * with `paths.*` so routes can change in one place.
 */
import { useParams } from 'react-router';
import { useProject } from '@/api/hooks/projects';
import type { Project } from '@/api/types';

export const SETTINGS_TABS = [
  'general',
  'classes',
  'attributes',
  'presets',
  'calibration',
  'automation',
  'members',
] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

export const paths = {
  home: () => '/',
  login: () => '/login',
  account: () => '/account',
  newProject: () => '/projects/new',
  project: (pid: number) => `/projects/${pid}`,
  upload: (pid: number) => `/projects/${pid}/upload`,
  exports: (pid: number) => `/projects/${pid}/exports`,
  projectJobs: (pid: number) => `/projects/${pid}/jobs`,
  models: (pid: number) => `/projects/${pid}/models`,
  settings: (pid: number, tab: SettingsTab = 'general') => `/projects/${pid}/settings/${tab}`,
  /** Annotator; `annotation` opens a specific annotation, `review` enables review mode. */
  image: (pid: number, iid: number, options: { annotation?: number; review?: boolean } = {}) => {
    const search = new URLSearchParams();
    if (options.annotation !== undefined) search.set('annotation', String(options.annotation));
    if (options.review) search.set('review', '1');
    const query = search.toString();
    return `/projects/${pid}/images/${iid}${query ? `?${query}` : ''}`;
  },
  adminUsers: () => '/admin/users',
  adminJobs: () => '/admin/jobs',
  adminSystem: () => '/admin/system',
} as const;

function parseId(raw: string | undefined, name: string): number {
  const id = Number(raw);
  if (!raw || !Number.isInteger(id) || id <= 0)
    throw new Error(`Invalid ${name} in the URL: ${raw ?? '(missing)'}`);
  return id;
}

/** `:pid` of the current route as a number. */
export function useProjectId(): number {
  return parseId(useParams().pid, 'project id');
}

/** `:iid` of the current route as a number. */
export function useImageId(): number {
  return parseId(useParams().iid, 'image id');
}

/**
 * The current project inside `/projects/:pid/*` routes. `ProjectLayout` renders its
 * children only once the project is loaded, so this never returns `undefined` there.
 * The annotator route is outside `ProjectLayout`: use `useProject(useProjectId())` there.
 */
export function useCurrentProject(): Project {
  const { data } = useProject(useProjectId());
  if (!data) throw new Error('useCurrentProject() used outside ProjectLayout');
  return data;
}
